import { create } from "zustand";
import type {
  Screen, AuthView, Tone, Quest, MainQuest, AttrDef,
  Account, Income, Expense, Debt, Payment, DebtPayment, Transfer, Person, MoodDay, BodyDay, MindCare, VaultFile, Note, DayInfo, WeatherInfo, DoneQuest, Cert, Game, UpcomingIncome, UpcomingExpense, UpcomingDebt, StandingCost, Outlook,
} from "../types";
import { canLive, loadConfig } from "../lib/config";
import { lastEmail, rememberEmail, passkeyEnrolled, setPasskeyEnrolled } from "../lib/prefs";
import { api, postApi, tokenClear, AUTH_EXPIRED_EVENT } from "../lib/api";
import * as auth from "../lib/auth";
import { pendingFinanceRequests, retainFinanceRequest, confirmFinanceRequest, clearFinanceRequests, financeLabel, type FinanceRequest } from "../lib/financeRequests";
import { clearCollectionRequests } from "../lib/collectionRequests";
import { cancelLiveLoads, goLive, refreshFinances, refreshVault } from "../lib/live";
import { apiComplete, apiUncomplete, apiClaim, dailyRewardEligible, fetchQuestLog, questMains } from "../lib/quests";
import { activeSteps, nextCycle } from "../lib/cadence";
import { DOMAINS, MAINS0, titleFor, xpNeeded, curDate, curMonth, isoToday, slug, TODAY0, WEATHER0 } from "../data/seed";

const digits = (s: string): string => s.replace(/[^0-9]/g, "");

// One store mirrors app.html's global `state`; methods mirror its ACTIONS table
// and core game mutators (award/complete/undo/toggleStep/claim). Every `set()`
// replaces the old imperative render(); controlled inputs replace the
// getElementById reads + preserveDrafts/pendingFocus machinery.
type Dict<T> = Record<string, T>;

export interface HudStore {
  screen: Screen;
  soonLabel: string;
  startupError: string;

  // auth / passkey / reset
  email: string; pass: string; showPass: boolean;
  authErr: string; authField: string; authBusy: boolean;
  authView: AuthView; authNote: string;
  resetCode: string; resetPass: string; showReset: boolean; resetDest: string;
  pkBusy: boolean; pkMsg: string; pkTone: Tone;

  // game
  quests: Quest[];
  done: Dict<boolean>; awarded: Dict<number>; stepDone: Dict<boolean>;
  skipped: Dict<boolean>; expanded: Dict<boolean>; finished: Dict<string>;
  xp: number; level: number; xpToday: number; streak: number;
  // The reward-secured day is separate from lastActive, which anchors the streak.
  lastActive: string | null; securedOn: string | null; streakCaught: boolean;
  claimed: boolean;
  // Real persistence state. `saving` counts writes in flight; `unsaved`
  // holds the ones that failed, so a dropped save is visible instead of
  // living only in console.warn.
  saving: number; unsaved: { key: string; label: string; detail?: string }[];
  live: boolean; liveAttrs: AttrDef[] | null; badgeIds: string[];
  mains: MainQuest[];
  filter: string; range: string; shelfOpen: boolean; showDone: boolean;
  levelUp: boolean; levelUpNote: string; burst: string | null; mainDone: string | null; badge: string | null;

  // add / edit drafts
  // draftMains / editMains are ordered: the first entry is the primary main
  // quest (the one whose colour the quest wears). Empty = no parent.
  adding: boolean; draftTitle: string; draftMains: string[]; draftDomain: string; draftXp: number; draftStep: string; draftSteps: string[];
  editingId: string | null; editTitle: string; editMains: string[]; editDomain: string; editXp: number;

  // drill-in
  attr: string | null; main: string; domain: string; focusId: string | null; secs: number; running: boolean;
  questId: string | null; // open quest detail page
  // Which screen the quest detail was opened FROM, so closing it returns there
  // instead of always dumping you on the HUD. `main` / `domain` below are not
  // cleared while the detail is open, so the screen name is all that is needed.
  questFrom: Screen | null;
  debtId: string | null; // open debt/loan detail page
  accountId: string | null; // open account detail page (money in and out)
  questLog: DoneQuest[]; logBusy: boolean; logError: string; // the completed-quest history (Quest Log)

  // learning
  certs: Cert[];

  // entertainment
  games: Game[];

  // health
  people: Person[]; moodWeek: MoodDay[]; bodyWeek: BodyDay[]; mindCare: MindCare[]; moodPick: boolean;

  // finances
  accounts: Account[]; incomes: Income[]; expenses: Expense[]; debts: Debt[]; payments: Payment[]; transfers: Transfer[]; debtPayments: DebtPayment[];
  upcomingIncome: UpcomingIncome[]; upcomingExpense: UpcomingExpense[]; upcomingDebt: UpcomingDebt[]; standing: StandingCost[];
  expenseId: string | null; // open expense detail page
  outlook: Outlook | null;
  financeError: string;
  ledger: string; expCat: string; expAccount: string; incAccount: string; mvFrom: string; mvTo: string; accType: string;
  addingIncome: boolean; addingExpense: boolean; addingAccount: boolean; movingMoney: boolean;
  incAmount: string; incSource: string; expAmount: string; expNote: string; mvAmount: string; accName: string; accBalance: string;

  // vault
  vaultOpen: Dict<boolean>; noteId: string | null; raw: boolean; vq: string;
  vaultTree: Dict<VaultFile[]> | null; vaultNotes: Dict<Note> | null; vaultLive: boolean;
  // vaultFailed distinguishes "the listing request failed" from "the vault is
  // empty" - the two used to render identically, which is how a broken endpoint
  // spent so long looking like a sync gap.
  vaultFailed: boolean; vaultLoading: boolean;
  // A note body the listing skipped is fetched on click, so it can be loading
  // or missing.
  noteLoading: boolean; noteFailed: boolean;

  // env
  today: DayInfo; weather: WeatherInfo;

  // ── lifecycle ──
  boot: () => Promise<void>;
  // Re-pull everything if the tab has been away long enough for the data to
  // have moved. Called by the focus listeners bound at boot, not from a view.
  syncIfStale: () => void;

  // ── auth ──
  setEmail: (v: string) => void; setPass: (v: string) => void; setResetCode: (v: string) => void; setResetPass: (v: string) => void;
  submitAuth: () => void; togglePass: () => void; signOut: () => void;
  goReset: () => void; backToSignin: () => void; toggleResetPass: () => void; sendResetCode: () => void; submitReset: () => void;
  passkeySignIn: () => void; setupPasskey: () => void;

  // ── nav ──
  goHud: () => void; goCharacter: () => void; goVault: () => void; goSettings: () => void;
  openMain: (id: string) => void; openDomain: (name: string) => void; openAttr: (name: string) => void;
  goQuests: () => void; startPinned: () => void; goSoon: (label: string) => void;
  openQuest: (id: string) => void; closeQuest: () => void;
  openDebt: (id: string) => void; closeDebt: () => void;
  openAccount2: (id: string) => void; closeAccount2: () => void;
  openExpense2: (id: string) => void; closeExpense2: () => void;
  openLog: () => void; closeLog: () => void; loadLog: () => Promise<void>;

  // ── game ──
  complete: (id: string) => void; undo: (id: string) => void; toggleStep: (id: string, i: number) => void;
  pin: (id: string) => void; skip: (id: string) => void; claim: () => void;
  toggleExpand: (id: string) => void; toggleDone: () => void; toggleShelf: () => void;
  setFilter: (label: string) => void; setRange: (key: string) => void;

  // ── focus timer ──
  focus: (id: string) => void; toggleTimer: () => void; exitFocus: () => void; completeFocus: () => void;

  // ── overlays ──
  closeLevelUp: () => void; closeMainDone: () => void; openBadge: (name: string) => void; closeBadge: () => void;

  // ── add / edit quest ──
  openAdd: () => void; openAddForMain: (id: string) => void; openAddHere: (domain: string) => void; cancelAdd: () => void;
  setDraftTitle: (v: string) => void; setDraftStep: (v: string) => void;
  draftPickMain: (id: string) => void; draftPickDomain: (name: string) => void;
  xpUp: () => void; xpDown: () => void; addStep: () => void; removeStep: (i: number) => void; saveQuest: () => void;
  startEdit: (id: string) => void; cancelEdit: () => void; setEditTitle: (v: string) => void;
  editPickMain: (id: string) => void; editPickDomain: (name: string) => void; editXpUp: () => void; editXpDown: () => void;
  saveEdit: () => void; removeQuest: (id: string) => void;
  // quest detail: task-breakdown + due editing on an existing quest
  addQuestStep: (id: string, text: string) => void; removeQuestStep: (id: string, i: number) => void; setQuestDue: (id: string, due: string) => void;

  // ── health ──
  openMood: () => void; setMood: (key: string) => void; logMoment: (id: string) => void;

  // ── finances ──
  openIncome: () => void; openExpense: () => void; openMove: () => void; openAccount: () => void;
  cancelIncome: () => void; cancelExpense: () => void; cancelMove: () => void; cancelAccount: () => void;
  setIncAccount: (id: string) => void; setExpCat: (key: string) => void; setExpAccount: (id: string) => void;
  setMvFrom: (id: string) => void; setMvTo: (id: string) => void; setAccType: (key: string) => void; setLedger: (key: string) => void;
  setIncAmount: (v: string) => void; setIncSource: (v: string) => void;
  setExpAmount: (v: string) => void; setExpNote: (v: string) => void;
  setMvAmount: (v: string) => void; setAccName: (v: string) => void; setAccBalance: (v: string) => void;
  saveIncome: () => void; saveExpense: () => void; saveMove: () => void; saveAccount: () => void;

  // ── vault ──
  retryVault: () => void;
  openNote: (id: string) => void; openVaultNote: (path: string) => void; toggleFolder: (name: string) => void; toggleRaw: () => void;
  retryUnsaved: () => void; setVq: (v: string) => void;
}

// Retry thunks for writes that failed, keyed so a repeat of the same write
// replaces rather than stacks. Kept out of store state: functions do not belong
// in a serialisable store.
const retryRunners = new Map<string, () => Promise<unknown>>();

let pkFlashTimer: ReturnType<typeof setTimeout> | null = null;
let burstTimer: ReturnType<typeof setTimeout> | null = null;
let catchTimer: ReturnType<typeof setTimeout> | null = null;
let focusTimer: ReturnType<typeof setInterval> | null = null;

// The HUD fetched once, at boot, and never again. Nothing is cached to
// localStorage, so a reload was always correct; the problem was the tab nobody
// reloads. One left open overnight kept rendering the previous day: quests
// created since were simply absent, and the Plan tab showed a cash position
// that had already been spent, which is worse than showing nothing because it
// looks current.
//
// Coming back to the tab is the moment the data is about to be read, so it is
// the cheapest honest trigger. Deliberately not polling: this is a single-user
// tool on a near-zero-cost budget and a socket or an interval would buy
// freshness nobody is watching for.
let lastLive = 0;
const REFETCH_AFTER_MS = 60_000;
let focusSyncBound = false;
let authExpiryBound = false;
let bootRun = 0;

export const useHudStore = create<HudStore>()((set, get) => {
  const raise = (msg: string, field?: string) => set({ authErr: msg, authField: field || "" });
  // Bound once at boot rather than from a component effect, because the
  // listener has to outlive whichever screen happens to be mounted.
  const bindFocusSync = () => {
    if (focusSyncBound || typeof document === "undefined") return;
    focusSyncBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") get().syncIfStale();
    });
    // A restored tab does not always fire visibilitychange, notably on iOS
    // Safari, and this HUD is used on a phone. Window focus covers the gap;
    // the staleness check makes the overlap free.
    window.addEventListener("focus", () => get().syncIfStale());
  };
  const bindAuthExpiry = () => {
    if (authExpiryBound || typeof window === "undefined") return;
    authExpiryBound = true;
    window.addEventListener(AUTH_EXPIRED_EVENT, () => {
      bootRun++;
      cancelLiveLoads();
      // Keep optimistic state + retry runners in memory. After signing in, the
      // user can retry rather than re-entering every action made while offline.
      set({
        screen: "login",
        live: false,
        vaultLoading: false,
        noteLoading: false,
        startupError: "",
        authBusy: false,
        authView: "signin",
        authErr: "",
        authField: "",
        authNote: "Your session expired. Sign in again, then retry your unsaved changes.",
      });
    });
  };
  // Every path that brings the app live goes through here, so the staleness
  // clock and the focus listeners are armed once per route in rather than
  // remembered separately at each of the four call sites.
  const startupFailed = () => set({ screen: "loading", startupError: "Your Life OS could not load. Check your connection and try again.", authBusy: false });
  const liveAndArm = async (): Promise<void> => {
    const run = bootRun;
    set({ screen: "loading", startupError: "" });
    try {
      await goLive();
      if (run !== bootRun || !sessionStorage.getItem("access_token") || get().screen === "login") return;
      restoreFinanceRetries();
      lastLive = Date.now();
      bindFocusSync();
    } catch (e) {
      // A valid session with a dropped request needs recovery, not a silent
      // jump to the sign-in form. Auth expiry has its own event above.
      if (run === bootRun && sessionStorage.getItem("access_token")) startupFailed();
      throw e;
    }
  };
  const pkFlash = (msg: string, tone: Tone) => {
    set({ pkMsg: msg, pkTone: tone });
    if (pkFlashTimer) clearTimeout(pkFlashTimer);
    pkFlashTimer = setTimeout(() => set({ pkMsg: "" }), 4600);
  };
  // Wrap a write so its outcome is visible. Optimistic as before: the UI has
  // already moved, this only records whether the server agreed.
  const track = (key: string, label: string, run: () => Promise<unknown>): void => {
    const session = bootRun;
    retryRunners.set(key, run);
    set((s) => ({ saving: s.saving + 1, unsaved: s.unsaved.filter((u) => u.key !== key) }));
    run()
      .then(() => { if (session === bootRun) retryRunners.delete(key); })
      .catch((e) => {
        if (session !== bootRun) return;
        console.warn(label + " failed:", (e as Error).message);
        set((s) => ({ unsaved: [...s.unsaved.filter((u) => u.key !== key), { key, label, detail: (e as Error).message }] }));
      })
      .finally(() => { if (session === bootRun) set((s) => ({ saving: Math.max(0, s.saving - 1) })); });
  };

  const prepareFinance = (input: Omit<FinanceRequest, "requestId">): FinanceRequest | null => {
    try { const request = retainFinanceRequest(input); set({ financeError: "" }); return request; }
    catch (error) { set({ financeError: error instanceof Error && /Retry/.test(error.message) ? error.message : "Enable browser storage, then save again. Your form has been kept." }); return null; }
  };
  const sendFinance = (request: FinanceRequest) => async () => {
    const session = bootRun;
    const response = await postApi("/api/finances", request);
    if (session !== bootRun || !get().live) return;
    const result = await response.json().catch(() => ({})) as { vaultSyncPending?: boolean };
    if (session !== bootRun || !get().live) return;
    if (result.vaultSyncPending) throw new Error("Recorded. Vault sync is pending. Retry to sync the note with the same operation ID.");
    confirmFinanceRequest(request);
    await refreshFinances();
  };
  const restoreFinanceRetries = () => {
    const pending = pendingFinanceRequests();
    for (const request of pending) retryRunners.set("finance:" + request.requestId, sendFinance(request));
    if (pending.length) set((s) => ({ unsaved: [
      ...s.unsaved.filter(u => !u.key.startsWith("finance:")),
      ...pending.map(request => ({ key: "finance:" + request.requestId, label: financeLabel(request) })),
    ] }));
  };

  const byId = (id: string): Quest | undefined => get().quests.find((q) => q.id === id);

  // Push a quest's whole step list. Keyed per quest, so rapid edits to the same
  // breakdown collapse into one retry entry rather than a queue of them.
  const saveSteps = (id: string, steps: string[]): void => {
    if (!get().live) return;
    track("quest:steps:" + id, "Quest breakdown", () => postApi("/api/quests", { action: "update", id, steps }));
  };

  // Central XP award - grants XP, rolls level-ups, fires the transient burst.
  const award = (amount: number, note: string) => {
    const s = get();
    let xp = s.xp;
    let level = s.level;
    const wasTitle = titleFor(level);
    xp += amount;
    const xpToday = s.xpToday + amount;
    let up = false;
    while (xp >= xpNeeded(level)) {
      xp -= xpNeeded(level);
      level += 1;
      up = true;
    }
    const nowTitle = titleFor(level);
    set({
      xp, level, xpToday,
      levelUp: up,
      levelUpNote: up && nowTitle !== wasTitle ? "New title earned. You are " + nowTitle + " now." : note || "",
      burst: "+" + amount + " XP",
    });
    if (burstTimer) clearTimeout(burstTimer);
    burstTimer = setTimeout(() => set({ burst: null }), 1500);
  };

  const completeQuest = (q: Quest | undefined) => {
    if (!q) return;
    const s = get();
    if (s.done[q.id]) return;
    const paid = s.awarded[q.id] || 0;
    const stepDone = { ...s.stepDone };
    (q.steps || []).forEach((_, i) => { stepDone[q.id + ":" + i] = true; });
    const stamp = isoToday();
    set({
      done: { ...s.done, [q.id]: true }, awarded: { ...s.awarded, [q.id]: q.xp }, stepDone,
      quests: s.quests.map((x) => (x.id === q.id ? { ...x, completed: stamp, updated: stamp } : x)),
    });
    // Persist to the server so the completion + XP survive a refresh and land in
    // the Quest Log (optimistic - parity with the finance forms). A recurring
    // checklist also sends its next cycle, so the engine brings it back on time
    // even when it was finished from the button rather than the last tick.
    const steps = q.steps || [];
    const cycle = steps.some((t) => /\ud83d\udd01/.test(t)) ? { recur: true, next: nextCycle(steps, isoToday().slice(0, 7)) } : {};
    if (s.live) track("quest:" + q.id, "Saving \u201c" + q.title + "\u201d", () => apiComplete(q.id, cycle));
    // The day's FIRST completion is the one that preserves the streak. Fire the
    // catch once, on that transition only, so the fifth quest of the day does
    // not get the same fanfare as the one that actually saved the day.
    // The engines stamp last_active with Manila's day (mcp-server/src/day.ts),
    // which is what isoToday() gives in the browser here, so the flame and the
    // engine agree on when the day turned over. They only diverge if this device
    // is in another timezone, which is a per-user setting Life OS does not have.
    const activeStamp = isoToday();
    if (s.lastActive !== activeStamp) {
      set({ lastActive: activeStamp, streakCaught: true });
      if (catchTimer) clearTimeout(catchTimer);
      catchTimer = setTimeout(() => set({ streakCaught: false }), 1600);
    }
    award(Math.max(0, q.xp - paid), "You cleared " + q.domain + ". " + q.title + " is done.");
    // Completing a task updates task progress, not the goal's declared outcome.
    // Inferring a goal award here also replayed its celebration after reload.
  };

  const undoQuest = (q: Quest | undefined) => {
    if (!q) return;
    const s = get();
    if (!s.done[q.id]) return;
    const paid = s.awarded[q.id] ?? q.xp;
    const done = { ...s.done }; delete done[q.id];
    const awarded = { ...s.awarded }; delete awarded[q.id];
    const stepDone = { ...s.stepDone };
    (q.steps || []).forEach((_, i) => { delete stepDone[q.id + ":" + i]; });
    set({
      done, awarded, stepDone, xp: Math.max(0, s.xp - paid), xpToday: Math.max(0, s.xpToday - paid),
      quests: s.quests.map((x) => (x.id === q.id ? { ...x, completed: undefined, updated: isoToday() } : x)),
    });
    if (s.live) track("quest:" + q.id, "Reopening \u201c" + q.title + "\u201d", () => apiUncomplete(q.id));
  };

  const openFocus = (q: Quest | undefined) => {
    if (!q) return;
    if (focusTimer) clearInterval(focusTimer);
    set({ screen: "focus", focusId: q.id, secs: 0, running: true });
    focusTimer = setInterval(() => set({ secs: get().secs + 1 }), 1000);
  };

  return {
    screen: "loading",
    soonLabel: "",
    startupError: "",

    // Warm login: prefill the last-used email so a returning visitor never retypes it.
    email: lastEmail(), pass: "", showPass: false,
    authErr: "", authField: "", authBusy: false,
    authView: "signin", authNote: "",
    resetCode: "", resetPass: "", showReset: false, resetDest: "",
    pkBusy: false, pkMsg: "", pkTone: "ok",

    quests: [],
    done: {}, awarded: {}, stepDone: {}, skipped: {}, expanded: {}, finished: {},
    xp: 0, level: 1, xpToday: 0, streak: 0,
    lastActive: null, securedOn: null, streakCaught: false,
    claimed: false, saving: 0, unsaved: [],
    live: false, liveAttrs: null, badgeIds: [],
    mains: MAINS0,
    filter: "All", range: "today", shelfOpen: false, showDone: false,
    levelUp: false, levelUpNote: "", burst: null, mainDone: null, badge: null,

    adding: false, draftTitle: "", draftMains: ["m1"], draftDomain: "Career", draftXp: 50, draftStep: "", draftSteps: [],
    editingId: null, editTitle: "", editMains: [], editDomain: "Career", editXp: 50,

    attr: null, main: "m1", domain: "Health", focusId: null, secs: 0, running: false, questId: null, questFrom: null, debtId: null, accountId: null,
    questLog: [], logBusy: false, logError: "",

    certs: [],
    games: [],

    people: [], moodWeek: [], bodyWeek: [], mindCare: [], moodPick: false,

    accounts: [], incomes: [], expenses: [], debts: [], payments: [], transfers: [], debtPayments: [], upcomingIncome: [], upcomingExpense: [], upcomingDebt: [], standing: [], expenseId: null, outlook: null, financeError: "",
    ledger: "activity", expCat: "food", expAccount: "a1", incAccount: "a1", mvFrom: "a1", mvTo: "a2", accType: "bank",
    addingIncome: false, addingExpense: false, addingAccount: false, movingMoney: false,
    incAmount: "", incSource: "", expAmount: "", expNote: "", mvAmount: "", accName: "", accBalance: "",

    vaultOpen: { Efforts: true }, noteId: null, raw: false, vq: "",
    vaultTree: null, vaultNotes: null, vaultLive: false, vaultFailed: false, vaultLoading: false,
    noteLoading: false, noteFailed: false,

    today: { ...TODAY0 }, weather: { ...WEATHER0 },

    async boot() {
      const run = ++bootRun;
      cancelLiveLoads();
      set({ screen: "loading", startupError: "" });
      bindAuthExpiry();
      try {
        await loadConfig();
        if (run !== bootRun) return;
        const code = new URLSearchParams(location.search).get("code");
        if (code) await auth.exchange(code);
        if (run !== bootRun) return;
        if (canLive() && sessionStorage.getItem("access_token")) {
          await liveAndArm();
          return;
        }
        set({ screen: "login" });
      } catch (e) {
        if (run !== bootRun) return;
        console.warn("startup failed:", (e as Error).message);
        startupFailed();
      }
    },

    // Re-pull everything when the tab comes back, if the data has had time to
    // go stale. Three guards, each for a way this could do harm rather than
    // good: a write in flight would be overwritten by the response it raced; a
    // failed write waiting to retry lives only in this tab, so a refetch would
    // silently discard it; and a signed-out tab has nothing to fetch with.
    syncIfStale() {
      if (!canLive() || !sessionStorage.getItem("access_token")) return;
      if (get().screen === "login" || get().screen === "loading") return;
      if (get().saving > 0 || get().unsaved.length > 0) return;
      if (Date.now() - lastLive < REFETCH_AFTER_MS) return;
      lastLive = Date.now();
      goLive(true).catch((e) => console.warn("focus refetch failed, keeping what is on screen:", (e as Error).message));
    },

    setEmail(v) { set((s) => ({ email: v, ...(s.authField === "email" ? { authErr: "", authField: "" } : {}) })); },
    setPass(v) { set((s) => ({ pass: v, ...(s.authField === "pass" ? { authErr: "", authField: "" } : {}) })); },
    setResetCode(v) { set((s) => ({ resetCode: v, ...(s.authField === "code" ? { authErr: "", authField: "" } : {}) })); },
    setResetPass(v) { set((s) => ({ resetPass: v, ...(s.authField === "newpass" ? { authErr: "", authField: "" } : {}) })); },

    submitAuth() {
      const s = get();
      if (s.authBusy) return;
      const email = s.email.trim();
      const pass = s.pass;
      set({ email, authNote: "" });
      if (!email) return raise("Enter your email.", "email");
      if (!auth.emailRe.test(email)) return raise("That email doesn't look right.", "email");
      if (canLive()) {
        if (!pass) return raise("Enter your password.", "pass");
        set({ authBusy: true, authErr: "", authField: "" });
        auth.passwordLogin(email, pass).then(() => { rememberEmail(email); return liveAndArm(); }).catch((e) => {
          set({ authBusy: false });
          raise((e as Error).message || "Sign-in failed.", "pass");
        });
        return;
      }
      if (pass.length < 8) return raise("Password needs at least 8 characters.", "pass");
      set({ screen: "hud" });
    },
    togglePass() { set((s) => ({ showPass: !s.showPass })); },
    signOut() {
      bootRun++;
      cancelLiveLoads();
      let storageBlocked = false;
      let refreshToken: string | null = null;
      try { refreshToken = sessionStorage.getItem("refresh_token"); } catch { storageBlocked = true; }
      if (canLive() && refreshToken) void auth.revokeRefreshToken(refreshToken).catch(() => console.warn("Refresh-token revocation was unavailable. The local session was cleared."));
      auth.cancelAuthentication();
      try { tokenClear(); } catch { storageBlocked = true; }
      retryRunners.clear();
      if (pkFlashTimer) clearTimeout(pkFlashTimer);
      if (burstTimer) clearTimeout(burstTimer);
      if (catchTimer) clearTimeout(catchTimer);
      if (focusTimer) clearInterval(focusTimer);
      for (const clear of [clearFinanceRequests, clearCollectionRequests, () => sessionStorage.removeItem("pkce_verifier"), () => sessionStorage.removeItem("oauth_state")]) {
        try { clear(); } catch { storageBlocked = true; }
      }
      lastLive = 0;
      set({ ...useHudStore.getInitialState(), screen: "login", email: get().email,
        ...(storageBlocked ? { authNote: "Browser storage could not be cleared. Close this tab to finish signing out." } : {}),
      });
    },
    goReset() {
      const em = get().email.trim();
      set({ ...(em ? { email: em } : {}), authView: "resetReq", authErr: "", authField: "", authNote: "", authBusy: false });
    },
    backToSignin() {
      set({ authView: "signin", authErr: "", authField: "", authBusy: false, resetCode: "", resetPass: "", authNote: "" });
    },
    toggleResetPass() { set((s) => ({ showReset: !s.showReset })); },
    sendResetCode() {
      const s = get();
      if (s.authBusy) return;
      const resend = s.authView === "resetConf";
      const email = (s.email || "").trim();
      set({ email });
      if (!email) return raise("Enter your email.", "email");
      if (!auth.emailRe.test(email)) return raise("That email doesn't look right.", "email");
      if (!canLive()) return raise("Password reset needs the live app.", "email");
      set({ authBusy: true, authErr: "", authField: "", authNote: "" });
      auth.forgotPassword(email).then((dest) => {
        set({ authBusy: false, resetDest: dest, authView: "resetConf", resetCode: "", ...(resend ? { authNote: "New code sent - check your email." } : {}) });
      }).catch((e) => {
        set({ authBusy: false });
        raise((e as Error).message || "Couldn't send the code.", "email");
      });
    },
    submitReset() {
      const s = get();
      if (s.authBusy) return;
      const code = s.resetCode.trim();
      const pass = s.resetPass;
      set({ resetCode: code, resetPass: pass });
      if (!code) return raise("Enter the code from your email.", "code");
      if (!pass) return raise("Enter a new password.", "newpass");
      if (!auth.pwOk(pass)) return raise("Your new password doesn't meet the requirements below.", "newpass");
      set({ authBusy: true, authErr: "", authField: "" });
      auth.confirmForgotPassword(s.email, code, pass).then(() => {
        set({ authBusy: false, authView: "signin", pass: "", resetCode: "", resetPass: "", authNote: "Password reset. Sign in with your new password." });
      }).catch((e) => {
        set({ authBusy: false });
        const msg = (e as Error).message || "Couldn't reset the password.";
        raise(msg, /code|not authorized|expired/i.test(msg) ? "code" : "newpass");
      });
    },
    passkeySignIn() {
      const s = get();
      if (s.authBusy) return;
      const email = s.email.trim();
      set({ email });
      if (!email) return raise("Enter your email to use a passkey.", "email");
      if (!auth.emailRe.test(email)) return raise("That email doesn't look right.", "email");
      set({ authBusy: true, authErr: "", authField: "" });
      auth.passkeyLogin(email).then(() => { rememberEmail(email); return liveAndArm(); }).catch((e: unknown) => {
        set({ authBusy: false });
        const err = e as { name?: string; message?: string };
        if (err && (err.name === "NotAllowedError" || err.name === "AbortError")) return;
        raise(err.message || "Passkey sign-in failed.", "email");
      });
    },
    setupPasskey() {
      const s = get();
      if (s.pkBusy) return;
      // Don't add a second passkey if this device already has one enrolled.
      if (passkeyEnrolled(s.email)) { pkFlash("A passkey is already set up on this device.", "ok"); return; }
      set({ pkBusy: true, pkMsg: "" });
      auth.registerPasskey().then(() => { set({ pkBusy: false }); setPasskeyEnrolled(get().email); pkFlash("Passkey added - use it to sign in next time.", "ok"); })
        .catch((e: unknown) => {
          set({ pkBusy: false });
          const err = e as { name?: string; message?: string };
          if (err && (err.name === "NotAllowedError" || err.name === "AbortError")) return;
          // The authenticator refuses a duplicate - record it so the UI reflects "added".
          if (err && err.name === "InvalidStateError") { setPasskeyEnrolled(get().email); pkFlash("A passkey is already set up on this device.", "ok"); return; }
          pkFlash(err.message || "Couldn't set up the passkey.", "err");
        });
    },

    goHud() { set({ screen: "hud", attr: null }); },
    goCharacter() { set({ screen: "character" }); },
    goSettings() { set({ screen: "settings", pkMsg: "" }); },
    goVault() { set({ screen: "vault" }); },
    openMain(id) { set({ screen: "main", main: id || "m1" }); },
    openDomain(name) { set({ screen: "domain", domain: name }); },
    openAttr(name) { set((s) => ({ attr: s.attr === name ? null : name })); },
    goQuests() { set({ range: "all", filter: "All" }); },
    startPinned() {
      const s = get();
      const p = s.quests.find((q) => q.pinned && !s.done[q.id]);
      if (p) openFocus(p);
      else set({ adding: true, screen: "hud" });
    },
    goSoon(label) { set({ screen: "soon", soonLabel: label }); },

    complete(id) { completeQuest(byId(id)); },
    undo(id) { undoQuest(byId(id)); },
    toggleStep(id, i) {
      const q = byId(id);
      if (!q) return;
      const key = q.id + ":" + i;
      const s = get();
      const steps = q.steps || [];
      const month = isoToday().slice(0, 7);
      // Only the steps in their month take part (lib/cadence.ts): a quarterly
      // premium has no box to tick in December.
      const active = activeSteps(steps, month);
      if (!active.includes(i)) return;
      // A recurring checklist (its steps carry a 🔁) resets monthly on the server;
      // the HUD tells the backend which kind this is, and when its next cycle
      // starts, which is when the engine brings the finished checklist back. Ticks
      // persist on the quest record now, so a reload or tab-switch keeps them.
      const recur = steps.some((t) => /🔁/.test(t));
      const next = nextCycle(steps, month);
      const persist = (done: boolean) => {
        if (get().live) track("quest:step:" + key, "Saving step", () => postApi("/api/quests", { action: "step", id: q.id, i, done, recur, next }));
      };

      // Unticking. A finished quest reopens first (undoQuest refunds its XP and
      // clears every step), so it lands back at zero rather than half-ticked.
      if (s.stepDone[key] || s.done[q.id]) {
        if (s.done[q.id]) { undoQuest(q); return; }
        const cleared = { ...s.stepDone };
        delete cleared[key];
        set({ stepDone: cleared });
        persist(false);
        return;
      }

      // Ticking is checkbox-only: a quest's XP is banked once, on completion.
      const stepDone = { ...s.stepDone, [key]: true };
      set({ stepDone });
      persist(true);
      const all = active.every((j) => !!stepDone[q.id + ":" + j]);
      // The last box banks the quest's XP. A recurring checklist completes too - the
      // engine reopens it for its next cycle (reopenStaleDailies), so a monthly bill
      // run pays out every month, like the dailies now do.
      if (all) completeQuest(q);
    },
    pin(id) {
      // Toggle: clicking the pinned quest clears it; otherwise pin this one and
      // unpin the rest (only one quest is "pinned for today"). Stays on the
      // current screen - pinning no longer yanks you to the HUD.
      set((s) => {
        const already = !!s.quests.find((x) => x.id === id)?.pinned;
        return { quests: s.quests.map((x) => ({ ...x, pinned: already ? false : x.id === id })) };
      });
    },
    skip(id) {
      const s = get();
      if (s.done[id]) return;
      set({ skipped: { ...s.skipped, [id]: !s.skipped[id] } });
    },
    claim() {
      const s = get();
      if (s.claimed || s.saving > 0) return;
      // Gated: required dailies must be done today when any are active; with no
      // required daily, one daily completion is enough (System/xp-rules.md).
      // "Today" is load-bearing: `quests` now carries the completed history, so
      // a bare `s.done[q.id]` would leave the gate open forever on the strength
      // of one daily finished months ago. The server checks done_at === today
      // (handlers/quests.ts, action "claim"); this mirrors it so the button is
      // not offered for a claim the API will reject with a 409.
      if (!dailyRewardEligible(s.quests, s.done, isoToday())) return;
      set({ claimed: true });
      // Optimistic, matching complete(). The server re-checks the gate and the
      // once-per-day rule, so a stale client cannot double-claim.
      if (s.live) track("claim", "Daily reward", () => apiClaim());
      award(40, "Daily reward claimed. Streak holds at " + s.streak + " days.");
    },
    toggleExpand(id) { set((s) => ({ expanded: { ...s.expanded, [id]: !s.expanded[id] } })); },
    toggleDone() { set((s) => ({ showDone: !s.showDone })); },
    toggleShelf() { set((s) => ({ shelfOpen: !s.shelfOpen })); },
    setFilter(label) { set({ filter: label }); },
    setRange(key) { set({ range: key }); },

    focus(id) { openFocus(byId(id)); },
    toggleTimer() {
      const s = get();
      if (s.running) { if (focusTimer) clearInterval(focusTimer); set({ running: false }); }
      else { focusTimer = setInterval(() => set({ secs: get().secs + 1 }), 1000); set({ running: true }); }
    },
    exitFocus() { if (focusTimer) clearInterval(focusTimer); set({ running: false, screen: "hud" }); },
    completeFocus() {
      if (focusTimer) clearInterval(focusTimer);
      const q = get().quests.find((x) => x.id === get().focusId);
      set({ running: false, screen: "hud" });
      if (q) completeQuest(q);
    },

    closeLevelUp() { set({ levelUp: false }); },
    closeMainDone() { set({ mainDone: null }); },
    openBadge(name) { set({ badge: name }); },
    closeBadge() { set({ badge: null }); },

    openAdd() { set({ adding: true, screen: "hud" }); },
    openAddForMain(id) { set({ adding: true, draftMains: [id] }); },
    // "Add here" from a domain page: open the form in place (no jump to the HUD)
    // with this domain pre-selected, so the new quest lands in the right area.
    openAddHere(domain) { set({ adding: true, draftDomain: domain }); },
    cancelAdd() { set({ adding: false, draftTitle: "", draftSteps: [], draftStep: "" }); },
    setDraftTitle(v) { set({ draftTitle: v }); },
    setDraftStep(v) { set({ draftStep: v }); },
    // Toggle, not select: a quest can serve several main quests, and the first
    // one picked stays primary.
    draftPickMain(id) {
      set((s) => ({ draftMains: s.draftMains.includes(id) ? s.draftMains.filter((m) => m !== id) : [...s.draftMains, id] }));
    },
    draftPickDomain(name) { set({ draftDomain: name }); },
    xpUp() { set((s) => ({ draftXp: Math.min(300, s.draftXp + 10) })); },
    xpDown() { set((s) => ({ draftXp: Math.max(10, s.draftXp - 10) })); },
    addStep() {
      const s = get();
      const t = s.draftStep.trim();
      if (t) set({ draftSteps: s.draftSteps.concat(t), draftStep: "" });
    },
    removeStep(i) { set((s) => ({ draftSteps: s.draftSteps.filter((_, j) => j !== i) })); },
    saveQuest() {
      const s = get();
      const title = s.draftTitle.trim();
      if (!title) return;
      const d = DOMAINS.find((x) => x.name === s.draftDomain);
      // The engine's own id shape (<day>-<slug>), sent along with the create so
      // the optimistic row and the stored one are the same quest after a refresh.
      // The old "n3_14" placeholder never reached a server, so it did not matter.
      const id = isoToday() + "-" + slug(title);
      const q: Quest = {
        id,
        title, domain: s.draftDomain,
        attr: (d && d.sub.split(" ·")[0]) || "Career",
        type: "Side quest", xp: s.draftXp, when: "today",
        main: s.draftMains[0] ?? "", mains: s.draftMains.slice(),
        steps: s.draftSteps.slice(), pinned: false,
        created: isoToday(), updated: isoToday(),
      };
      set({ quests: [q, ...s.quests], adding: false, draftTitle: "", draftSteps: [], draftStep: "" });
      // Persist it (optimistic, like the finance forms). Deliberately NOT
      // `today: true`: the engine's today flag means the day's ONE thing
      // (System/xp-rules.md), and there is only ever one. `when: "today"` above
      // is just so the quest you have this second typed is visible in the
      // default Today range instead of vanishing; on the next load it settles
      // into Week, which is where a quest you merely added belongs.
      if (s.live) {
        track("quest:new:" + id, "New quest “" + title + "”", () =>
          postApi("/api/quests", {
            action: "create", id, title, domain: s.draftDomain, xp: s.draftXp,
            quest_type: "side", goals: q.mains, steps: q.steps,
          }));
      }
    },
    startEdit(id) {
      const s = get();
      if (s.editingId === id) { set({ editingId: null }); return; }
      const q = s.quests.find((x) => x.id === id);
      if (!q) return;
      set({ editingId: id, editTitle: q.title, editMains: questMains(q), editDomain: q.domain, editXp: q.xp });
    },
    cancelEdit() { set({ editingId: null }); },
    setEditTitle(v) { set({ editTitle: v }); },
    editPickMain(id) {
      set((s) => ({ editMains: s.editMains.includes(id) ? s.editMains.filter((m) => m !== id) : [...s.editMains, id] }));
    },
    editPickDomain(name) { set({ editDomain: name }); },
    editXpUp() { set((s) => ({ editXp: Math.min(300, s.editXp + 10) })); },
    editXpDown() { set((s) => ({ editXp: Math.max(10, s.editXp - 10) })); },
    saveEdit() {
      const s = get();
      const t = s.editTitle.trim();
      if (!t) return;
      const id = s.editingId;
      set({
        quests: s.quests.map((q) =>
          q.id === s.editingId
            ? { ...q, title: t, main: s.editMains[0] ?? "", mains: s.editMains.slice(), domain: s.editDomain, xp: s.editXp, updated: isoToday() }
            : q,
        ),
        editingId: null,
      });
      if (s.live && id) {
        track("quest:edit:" + id, "Saving “" + t + "”", () =>
          postApi("/api/quests", {
            action: "update", id, title: t, domain: s.editDomain, xp: s.editXp, goals: s.editMains,
          }));
      }
    },
    removeQuest(id) {
      const s = get();
      const done = { ...s.done }; delete done[id];
      const onDetail = s.screen === "quest" && s.questId === id;
      set({ done, quests: s.quests.filter((x) => x.id !== id), editingId: null, ...(onDetail ? { screen: "hud", questId: null } : {}) });
      // Soft delete on the server: the item goes, the note moves to .trash/.
      if (s.live) track("quest:del:" + id, "Removing quest", () => postApi("/api/quests", { action: "delete", id }));
    },

    // Quest detail page: open/close, and edit an existing quest's breakdown + due.
    openQuest(id) { set((s) => ({ screen: "quest", questId: id, questFrom: s.screen === "quest" ? s.questFrom : s.screen })); },
    closeQuest() { set((s) => ({ screen: s.questFrom ?? "hud", questId: null, questFrom: null })); },
    // Debt/loan detail page: opened from a debt row in the Finances module.
    // Returns to the Finances domain page (where the debt list lives).
    openDebt(id) { set({ screen: "debt", debtId: id }); },
    closeDebt() { set({ screen: "domain", domain: "Finances", debtId: null }); },
    // Account detail page (money in and out), opened from an account card in the
    // Finances module. `openAccount` is the add-account form, so this carries the
    // 2, exactly like openExpense2 above.
    openAccount2(id) { set({ screen: "account", accountId: id }); },
    closeAccount2() { set({ screen: "domain", domain: "Finances", accountId: null }); },
    // `openExpense` was taken by the log-an-expense form, so these carry the 2.
    // Renaming the older one would touch every call site for no gain.
    openExpense2(id) { set({ screen: "expense", expenseId: id }); },
    closeExpense2() { set({ screen: "domain", domain: "Finances", expenseId: null }); },
    // Quest Log - the completed-quest history (GET /api/quests?status=done).
    openLog() { set({ screen: "log" }); }, // QuestLog loads on mount
    closeLog() { set({ screen: "hud" }); },
    async loadLog() {
      if (!get().live) { set({ questLog: [], logError: "" }); return; }
      const session = bootRun;
      set({ logBusy: true, logError: "" });
      try { const list = await fetchQuestLog(); if (session === bootRun && get().live) set({ questLog: Array.isArray(list) ? list : [] }); }
      catch (e) { if (session === bootRun) set({ logError: "Your history could not be loaded. Please try again." }); }
      finally { if (session === bootRun) set({ logBusy: false }); }
    },
    // The three below edit an existing quest from its detail page. They persist
    // for the same reason saveEdit does: a breakdown that disappears on refresh
    // is worse than no breakdown, because you trust it and then lose it. Steps
    // live in the note as Obsidian task lines, which is where the HUD reads them
    // back from, so this round-trips through the vault.
    addQuestStep(id, text) {
      const t = text.trim();
      if (!t) return;
      const q = byId(id);
      if (!q) return;
      const steps = (q.steps || []).concat(t);
      set((s) => ({
        quests: s.quests.map((x) => (x.id === id ? { ...x, steps, updated: isoToday() } : x)),
      }));
      saveSteps(id, steps);
    },
    removeQuestStep(id, i) {
      const before = byId(id);
      if (!before) return;
      const steps = (before.steps || []).filter((_, j) => j !== i);
      set((s) => {
        const n = (before.steps || []).length;
        // Re-key stepDone: drop i..n-1, then shift the completed flags after i down one.
        const stepDone = { ...s.stepDone };
        for (let j = i; j < n; j++) delete stepDone[id + ":" + j];
        for (let j = i + 1; j < n; j++) { if (s.stepDone[id + ":" + j]) stepDone[id + ":" + (j - 1)] = true; }
        return {
          quests: s.quests.map((x) => (x.id === id ? { ...x, steps, updated: isoToday() } : x)),
          stepDone,
        };
      });
      saveSteps(id, steps);
    },
    setQuestDue(id, due) {
      set((s) => ({
        quests: s.quests.map((x) => (x.id === id ? { ...x, due: due || undefined, updated: isoToday() } : x)),
      }));
      if (get().live) {
        track("quest:due:" + id, "Due date", () => postApi("/api/quests", { action: "update", id, due: due || null }));
      }
    },

    openMood() { set((s) => ({ moodPick: !s.moodPick })); },
    setMood(key) {
      const s = get();
      const li = s.moodWeek.length - 1;
      const moodWeek = s.moodWeek.map((m, i) => (i === li ? { ...m, mood: key } : m));
      set({ moodWeek, moodPick: false });
      if (s.live) track("mood", "Mood", () => postApi("/api/health", { moodWeek, bodyWeek: s.bodyWeek, mindCare: s.mindCare }));
      award(15, "");
    },
    logMoment(id) {
      const s = get();
      set({ people: s.people.map((p) => (p.id === id ? { ...p, days: 0 } : p)) });
      if (s.live) track("moment:" + id, "Time together", () => postApi("/api/relationships", { id }));
      award(25, "");
    },

    // Finance forms - opening one clears the others (parity with the prototype).
    openIncome() { set({ addingIncome: true, addingExpense: false, addingAccount: false, movingMoney: false }); },
    openExpense() { set({ addingExpense: true, addingIncome: false, addingAccount: false, movingMoney: false }); },
    openMove() { set({ movingMoney: true, addingIncome: false, addingExpense: false, addingAccount: false }); },
    openAccount() { set({ addingAccount: true, addingIncome: false, addingExpense: false, movingMoney: false }); },
    cancelIncome() { set({ addingIncome: false, incAmount: "", incSource: "" }); },
    cancelExpense() { set({ addingExpense: false, expAmount: "", expNote: "" }); },
    cancelMove() { set({ movingMoney: false, mvAmount: "" }); },
    cancelAccount() { set({ addingAccount: false, accName: "", accBalance: "" }); },
    setIncAccount(id) { set({ incAccount: id }); },
    setExpCat(key) { set({ expCat: key }); },
    setExpAccount(id) { set({ expAccount: id }); },
    setMvFrom(id) { set((s) => ({ mvFrom: id, ...(s.mvTo === id ? { mvTo: s.mvFrom } : {}) })); },
    setMvTo(id) { set((s) => ({ mvTo: id, ...(s.mvFrom === id ? { mvFrom: s.mvTo } : {}) })); },
    setAccType(key) { set({ accType: key }); },
    setLedger(key) { set({ ledger: key }); },
    setIncAmount(v) { set({ incAmount: digits(v) }); },
    setIncSource(v) { set({ incSource: v }); },
    setExpAmount(v) { set({ expAmount: digits(v) }); },
    setExpNote(v) { set({ expNote: v }); },
    setMvAmount(v) { set({ mvAmount: digits(v) }); },
    setAccName(v) { set({ accName: v }); },
    setAccBalance(v) { set({ accBalance: digits(v) }); },

    saveIncome() {
      const s = get();
      const amt = parseInt(s.incAmount, 10);
      if (!amt) return;
      const src = s.incSource.trim() || "Income";
      const id = "i" + crypto.randomUUID();
      const request = s.live ? prepareFinance({ action: "income", id, source: src, account: s.incAccount, amount: amt }) : null;
      if (s.live && !request) return;
      set({
        incomes: [{ id, date: curDate(), source: src, account: s.incAccount, amount: amt, month: curMonth() }, ...s.incomes],
        accounts: s.accounts.map((a) => (a.id === s.incAccount ? { ...a, balance: a.balance + amt } : a)),
        addingIncome: false, incAmount: "", incSource: "",
      });
      if (request) track("finance:" + request.requestId, "Income", sendFinance(request));
    },
    saveExpense() {
      const s = get();
      const amt = parseInt(s.expAmount, 10);
      if (!amt) return;
      const note = s.expNote.trim() || "Spending";
      const id = "e" + crypto.randomUUID();
      const request = s.live ? prepareFinance({ action: "expense", id, note, cat: s.expCat, account: s.expAccount, amount: amt }) : null;
      if (s.live && !request) return;
      set({
        expenses: [{ id, date: curDate(), note, cat: s.expCat, account: s.expAccount, amount: amt, month: curMonth() }, ...s.expenses],
        accounts: s.accounts.map((a) => (a.id === s.expAccount ? { ...a, balance: a.balance - amt } : a)),
        addingExpense: false, expAmount: "", expNote: "",
      });
      if (request) track("finance:" + request.requestId, "Expense", sendFinance(request));
    },
    saveMove() {
      const s = get();
      const amt = parseInt(s.mvAmount, 10);
      if (!amt || s.mvFrom === s.mvTo) return;
      const from = s.mvFrom;
      const to = s.mvTo;
      // The id is minted here and sent, so the optimistic row and the stored
      // one are the same record rather than two that happen to look alike.
      const id = "t" + crypto.randomUUID();
      const nameOf = (accId: string) => s.accounts.find((a) => a.id === accId)?.name || accId;
      const note = `${nameOf(from)} to ${nameOf(to)}`;
      const request = s.live ? prepareFinance({ action: "transfer", id, from, to, amount: amt, note }) : null;
      if (s.live && !request) return;
      set({
        accounts: s.accounts.map((a) => (a.id === from ? { ...a, balance: a.balance - amt } : a.id === to ? { ...a, balance: a.balance + amt } : a)),
        // A transfer used to move both balances and leave nothing behind, so a
        // month later the jump had no explanation attached to it.
        transfers: [{ id, date: curDate(), note, from, to, amount: amt, month: curMonth() }, ...s.transfers],
        movingMoney: false, mvAmount: "",
      });
      if (request) track("finance:" + request.requestId, "Transfer", sendFinance(request));
    },
    saveAccount() {
      const s = get();
      const n = s.accName.trim();
      if (!n) return;
      const id = "a" + crypto.randomUUID();
      const bal = parseInt(s.accBalance, 10) || 0;
      const request = s.live ? prepareFinance({ action: "account", id, name: n, kind: s.accType, balance: bal }) : null;
      if (s.live && !request) return;
      set({
        accounts: [...s.accounts, { id, name: n, type: s.accType, balance: bal }],
        addingAccount: false, accName: "", accBalance: "",
      });
      if (request) track("finance:" + request.requestId, "Account", sendFinance(request));
    },

    // Vault
    retryVault() { if (get().live && !get().vaultLoading) void refreshVault(); },
    // The listing carries note bodies only up to a size budget, so a note it had
    // to skip is fetched here and cached into vaultNotes. In practice every note
    // in the vault fits and this never fires; it's the escape hatch that stops one
    // oversized note from taking the whole listing down, as it used to.
    openNote(id) {
      set({ noteId: id, noteFailed: false });
      const s = get();
      if (!s.live || !id || s.vaultNotes?.[id]) return; // demo mode, or already cached
      const session = bootRun;
      set({ noteLoading: true });
      api<Note>("/api/vault/note?path=" + encodeURIComponent(id))
        .then((n) => {
          // Ignore a response that lost the race to a newer click.
          if (session !== bootRun || !get().live || get().noteId !== id) return;
          set((cur) => ({ vaultNotes: { ...(cur.vaultNotes || {}), [id]: n }, noteLoading: false }));
        })
        .catch((e) => {
          console.warn("/api/vault/note failed:", (e as Error).message);
          if (session !== bootRun || !get().live || get().noteId !== id) return;
          set({ noteLoading: false, noteFailed: true });
        });
    },
    openVaultNote(path) { set({ screen: "vault", vq: "" }); get().openNote(path); },
    toggleFolder(name) { set((s) => ({ vaultOpen: { ...s.vaultOpen, [name]: !s.vaultOpen[name] } })); },
    toggleRaw() { set((s) => ({ raw: !s.raw })); },
    retryUnsaved() {
      // Re-run every write that failed. Nothing to do with Remotely Save: that
      // is an Obsidian desktop plugin the HUD cannot see or trigger.
      const stuck = get().unsaved;
      stuck.forEach((u) => {
        const run = retryRunners.get(u.key);
        if (run) track(u.key, u.label, run);
      });
    },
    setVq(v) { set({ vq: v }); },
  };
});
